import { Card, Radio, Spin } from 'antd';
import { getMemberPointHistoryDailyStats } from 'apis/ums';
import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import getClientTimeZone from 'utils/getClientTimeZone';

type StatsDays = 7 | 14 | 30;

type DailyPoint = {
  date: string;
  issued: number;
  used: number;
};

const DAYS_OPTIONS: { label: string; value: StatsDays }[] = [
  { label: '近7天', value: 7 },
  { label: '近2周', value: 14 },
  { label: '近1个月', value: 30 },
];

const formatPoint = (value?: number | null) => {
  if (value == null) return '-';
  return `${Number(value).toLocaleString()} 积分`;
};

const PointHistoryTrend = () => {
  const [days, setDays] = useState<StatsDays>(7);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<DailyPoint[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const { list: nextList } = await getMemberPointHistoryDailyStats({
          days,
          timeZone: getClientTimeZone(),
        });
        if (!cancelled) {
          setList(nextList);
        }
      } catch (err) {
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [days]);

  const totals = useMemo(() => {
    return list.reduce(
      (acc, item) => ({
        issued: acc.issued + item.issued,
        used: acc.used + item.used,
      }),
      { issued: 0, used: 0 },
    );
  }, [list]);

  const chartData = useMemo(
    () =>
      list.map((item) => ({
        ...item,
        dateLabel: item.date.slice(5),
      })),
    [list],
  );

  return (
    <Card
      bodyStyle={{ padding: '16px' }}
      title="积分趋势"
      extra={
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={days}
          onChange={(event) => setDays(event.target.value as StatsDays)}
          options={DAYS_OPTIONS}
        />
      }
    >
      <Spin spinning={loading}>
        <div className="mb-4 flex flex-wrap gap-8">
          <div>
            <div className="text-xs text-gray-400">合计发放</div>
            <div className="text-xl font-semibold text-gray-900">
              {formatPoint(totals.issued)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400">合计使用</div>
            <div className="text-xl font-semibold text-gray-900">
              {formatPoint(totals.used)}
            </div>
          </div>
        </div>

        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dateLabel" />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value, name) => [formatPoint(Number(value)), name]}
                labelFormatter={(label, payload) =>
                  payload?.[0]?.payload?.date || label
                }
              />
              <Legend />
              <Bar
                dataKey="issued"
                name="发放"
                fill="#1677ff"
                isAnimationActive={false}
              />
              <Bar
                dataKey="used"
                name="使用"
                fill="#fa8c16"
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Spin>
    </Card>
  );
};

export default PointHistoryTrend;
